<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  testimonials: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'edit', 'delete']);

// Format date
const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const handleAdd = () => {
  emit('add');
};

const handleEdit = item => {
  emit('edit', item);
};

const handleDelete = item => {
  emit('delete', item);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Add Button -->
    <div class="flex justify-end">
      <Button
        color="primary"
        icon="i-lucide-plus"
        label="Add Testimonial"
        class="!bg-woot-500 hover:!bg-woot-600"
        @click="handleAdd"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="i-lucide-loader-2 w-8 h-8 text-woot-500 animate-spin" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="testimonials.length === 0"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <span class="i-lucide-message-circle w-16 h-16 text-n-slate-6 mb-4" />
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        No testimonials yet
      </h3>
      <p class="text-sm text-n-slate-10 mb-6 max-w-md">
        Add customer testimonials to help the AI provide social proof and build
        trust.
      </p>
      <Button
        color="primary"
        icon="i-lucide-plus"
        label="Add Testimonial"
        class="!bg-woot-500 hover:!bg-woot-600"
        @click="handleAdd"
      />
    </div>

    <!-- Testimonial Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="item in testimonials"
        :key="item.id"
        class="group relative bg-white dark:bg-n-slate-2 border border-n-slate-3 rounded-xl overflow-hidden hover:border-woot-500 hover:shadow-lg transition-all"
      >
        <!-- Thumbnail -->
        <div class="relative aspect-video bg-n-slate-2 overflow-hidden">
          <img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            :alt="item.title"
            class="w-full h-full object-cover"
          />
          <div
            v-else
            class="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30"
          >
            <span class="i-lucide-quote w-12 h-12 text-purple-400" />
          </div>

          <!-- Language Badge -->
          <div
            v-if="item.language"
            class="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-xs font-medium"
          >
            {{ item.language }}
          </div>

          <!-- Actions Overlay -->
          <div
            class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
          >
            <button
              class="p-2 bg-white/90 hover:bg-white rounded-full text-n-slate-12 transition-colors"
              @click.stop="handleEdit(item)"
            >
              <span class="i-lucide-edit-2 w-4 h-4" />
            </button>
            <button
              class="p-2 bg-white/90 hover:bg-white rounded-full text-ruby-9 transition-colors"
              @click.stop="handleDelete(item)"
            >
              <span class="i-lucide-trash-2 w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 line-clamp-1 mb-2">
            {{ item.title }}
          </h3>
          <p class="text-xs text-n-slate-10 line-clamp-3 mb-3">
            {{ item.content }}
          </p>

          <!-- Meta -->
          <div class="flex flex-col gap-1 text-xs text-n-slate-9">
            <div v-if="item.createdAt">
              Created: {{ formatDate(item.createdAt) }}
            </div>
            <div v-if="item.updatedAt">
              Updated: {{ formatDate(item.updatedAt) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
